import * as THREE from 'three'
import { calculateMeshVolume } from '@/graphics/csg-operations'
import {
  createMeshFromInequalities,
  findIntegerPointsInPolyhedron,
  computeAABBFromInequalities,
  generateVerticesFromConstraints,
} from '@/graphics/geometry'
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import { addAxes } from '@/graphics/axes'
import { IntegerProblem, getCutInfo, Inequality } from '@/core/cuts'
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js'
import { logger } from '@/app/logger'

export function findMainPolyhedronMesh(group: THREE.Group | null): THREE.Mesh | null {
  if (!group) return null
  const mesh = group.children.find((child) => {
    if (!(child instanceof THREE.Mesh)) return false
    const mat: any = child.material as any
    const isWire = Array.isArray(mat) ? mat.some((m: any) => m?.wireframe) : mat?.wireframe
    const isSphere = (child.geometry as any)?.type === 'SphereGeometry'
    return !isWire && !isSphere
  }) as THREE.Mesh | undefined
  return mesh || null
}

export function updateMainMesh(group: THREE.Group, newMesh: THREE.Mesh): void {
  const oldMesh = findMainPolyhedronMesh(group)
  if (oldMesh) group.remove(oldMesh)
  group.add(newMesh)
}

export function computeGroupVolume(group: THREE.Group | null): number {
  const mesh = findMainPolyhedronMesh(group)
  if (!mesh) return 0
  return calculateMeshVolume(mesh.geometry)
}

export class PolyhedronManager {
  private scene: THREE.Scene

  constructor(scene: THREE.Scene) {
    this.scene = scene
  }

  disposeGroup(group: THREE.Group | null): void {
    if (!group) return
    this.scene.remove(group)
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose()
        const mat: any = child.material as any
        if (Array.isArray(mat)) mat.forEach((m: any) => m?.dispose?.())
        else mat?.dispose?.()
      }
    })
  }

  buildGroupFromInequalities(inequalities: Inequality[], options: { addEdges?: boolean; equalityPlaneFallback?: boolean } = {}): THREE.Group {
    const { addEdges = true, equalityPlaneFallback = true } = options
    // For the main game polyhedron, a completely broken geometry is better shown as
    // an invisible placeholder than as a misleading fallback cube.
    const mesh = createMeshFromInequalities(inequalities, {
      equalityPlaneFallback,
      errorFallbackInvisible: true
    })
    const group = new THREE.Group()
    group.add(mesh)
    if (addEdges) {
      // Keep only real silhouette/face edges; 1° threshold filters coplanar triangle edges
      const pos: any = (mesh.geometry as any)?.attributes?.position
      const hasVerts = pos && typeof pos.count === 'number' && pos.count > 0
      if (hasVerts) {
        const edgesGeometry = new THREE.EdgesGeometry(mesh.geometry, 1)
        const wireframe = new THREE.LineSegments(
          edgesGeometry,
          new THREE.LineBasicMaterial({ color: 0x5dade2, transparent: true, opacity: 0.85, linewidth: 1, depthTest: false })
        )
        wireframe.renderOrder = 50
        group.add(wireframe)
      }
    }
    this.scene.add(group)
    return group
  }

  addIntegerPointsAndAxes(
    group: THREE.Group,
    problem: IntegerProblem,
    options: { showHull?: boolean; overrideLPSolution?: number[]; hasFractionalSolution?: boolean } = {}
  ): void {
    const { showHull = false, overrideLPSolution, hasFractionalSolution: hasFracOverride } = options
    // Global safety: remove any previous LP markers or integer hulls
    const toRemoveGlobal: THREE.Object3D[] = []
    this.scene.traverse((obj) => {
      const isLpMarker = (obj instanceof THREE.Mesh && obj.userData.lpMarker)
      const isIntHull = (obj instanceof THREE.LineSegments && obj.userData.intHull)
      const isAxis = obj.name === 'axis-group'
      const isAxisLabel = (obj as any) instanceof CSS2DObject && (obj as any).element?.classList?.contains?.('axis-label')
      if (isLpMarker || isIntHull || isAxis || isAxisLabel) {
        toRemoveGlobal.push(obj)
      }
    })

    toRemoveGlobal.forEach((obj) => {
      obj.parent?.remove(obj)
      if (obj instanceof THREE.Mesh || obj instanceof THREE.LineSegments) {
        obj.geometry.dispose()
        const mat = (obj as THREE.Mesh).material
        if (Array.isArray(mat)) {
          mat.forEach((m) => m.dispose())
        } else if (mat) {
          ; (mat as any).dispose?.()
        }
      }
    })
    // Decide scan bounds for integer points: if non-negativity not present, allow small negatives for visualization
    // Compute aabb via vertex enumeration (LP extreme equivalence)
    const aabb = computeAABBFromInequalities(problem.inequalities)
    const integerPoints = findIntegerPointsInPolyhedron(problem.inequalities, {
      xMin: aabb.xMin, xMax: aabb.xMax,
      yMin: aabb.yMin, yMax: aabb.yMax,
      zMin: aabb.zMin, zMax: aabb.zMax
    })
    logger.log(`Found ${integerPoints.length} integer points in feasible region`)

    // Optimal LP solution marker
    let optimalSolution: number[] = []
    let hasFractionalSolution = false
    try {
      if (overrideLPSolution !== undefined) {
        if (overrideLPSolution.length === 3) {
          optimalSolution = overrideLPSolution
          hasFractionalSolution = !!hasFracOverride
        } else {
          optimalSolution = []
        }
      } else {
        const cutInfo = getCutInfo(problem)
        optimalSolution = cutInfo.optimalSolution
        hasFractionalSolution = cutInfo.hasFractionalSolution

        // Snap global LP solution to a true vertex of the rendered
        // polyhedron so that the orange marker always sits on a
        // geometric vertex, not merely a face point from the LP
        // tableau projection. This mirrors the BnB node snapping
        // logic and keeps the HUD numbers and 3D geometry coherent.
        try {
          const verts = generateVerticesFromConstraints(problem.inequalities)
          if (Array.isArray(verts) && verts.length > 0 && Array.isArray(optimalSolution) && optimalSolution.length >= 3) {
            const c = problem.c || []
            let bestVal = Number.NEGATIVE_INFINITY
            const bestVerts: typeof verts = []

            verts.forEach((v: THREE.Vector3) => {
              const val =
                (c[0] || 0) * v.x +
                (c[1] || 0) * v.y +
                (c[2] || 0) * v.z
              if (val > bestVal + 1e-9) {
                bestVal = val
                bestVerts.length = 0
                bestVerts.push(v)
              } else if (Math.abs(val - bestVal) <= 1e-9) {
                bestVerts.push(v)
              }
            })

            if (bestVerts.length > 0 && Number.isFinite(bestVal)) {
              let chosen = bestVerts[0]
              let bestDist = Infinity
              const sx = optimalSolution[0]
              const sy = optimalSolution[1]
              const sz = optimalSolution[2]
              for (const v of bestVerts) {
                const dx = v.x - sx
                const dy = v.y - sy
                const dz = v.z - sz
                const d2 = dx * dx + dy * dy + dz * dz
                if (d2 < bestDist) {
                  bestDist = d2
                  chosen = v
                }
              }

              const snapped = [chosen.x, chosen.y, chosen.z]
              const snappedHasFrac = snapped.some((vv) => Math.abs(vv - Math.round(vv)) > 1e-9)

              if (Number.isFinite(cutInfo.optimalValue) && Math.abs(bestVal - cutInfo.optimalValue) > 1e-6) {
                logger.warn('[PolyhedronManager] Vertex-based LP value differs from simplex value for global problem', {
                  simplexValue: cutInfo.optimalValue,
                  vertexValue: bestVal,
                  simplexSolution: optimalSolution,
                  snappedSolution: snapped,
                })
              }

              optimalSolution = snapped
              hasFractionalSolution = snappedHasFrac
            }
          }
        } catch (snapErr) {
          logger.warn('[PolyhedronManager] Failed to snap global LP solution to vertex:', snapErr)
        }
      }
    } catch (e) {
      logger.warn('Could not get optimal LP solution:', e)
    }

    // Remove previous LP marker if exists in this group
    // Remove previous LP marker meshes
    const toRemove: THREE.Object3D[] = []
    for (const child of group.children) {
      if (child instanceof THREE.Mesh && (child as any).userData?.lpMarker) {
        toRemove.push(child)
      }
    }
    toRemove.forEach((obj) => {
      group.remove(obj)
        ; (obj as THREE.Mesh).geometry.dispose()
    })

    if (optimalSolution.length === 3) {
      this.addLPMarker(group, optimalSolution, hasFractionalSolution)
    }

    const regularPointMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1.0, depthTest: false, depthWrite: false })
    integerPoints.forEach((point) => {
      const isOptimal =
        optimalSolution.length === 3 &&
        Math.abs(point.x - optimalSolution[0]) < 0.001 &&
        Math.abs(point.y - optimalSolution[1]) < 0.001 &&
        Math.abs(point.z - optimalSolution[2]) < 0.001
      if (isOptimal) return
      const sphereGeometry = new THREE.SphereGeometry(0.025, 16, 16)
      const pointMesh = new THREE.Mesh(sphereGeometry, regularPointMaterial.clone())
      pointMesh.renderOrder = 100
      pointMesh.position.set(point.x, point.y, point.z)
      group.add(pointMesh)
    })

    // Convex hull wireframe of integer points (optional)
    if (showHull && integerPoints.length >= 4) {
      try {
        const convexGeometry = new ConvexGeometry(integerPoints)
        const wireframeGeometry = new THREE.EdgesGeometry(convexGeometry)
        const wireframeMaterial = new THREE.LineBasicMaterial({ color: 0xffd700, linewidth: 2, transparent: true, opacity: 0.75, depthTest: false })
        const convexHullWireframe = new THREE.LineSegments(wireframeGeometry, wireframeMaterial)
          ; (convexHullWireframe as any).userData = { ...(convexHullWireframe as any).userData, intHull: true }
        convexHullWireframe.renderOrder = 100
        group.add(convexHullWireframe)
      } catch (e) {
        logger.warn('Failed to create integer points convex hull:', e)
      }
    }

    // Axes
    addAxes(this.scene, integerPoints)
  }

  public addLPMarker(group: THREE.Group, solution: number[], isFractional: boolean): void {
    if (solution.length !== 3) return

    const lpSolutionPoint = new THREE.Vector3(solution[0], solution[1], solution[2])
    const lpMaterial = new THREE.MeshBasicMaterial({
      color: 0xff6b35,
      transparent: true,
      opacity: 0.95,
      depthTest: false,
      depthWrite: false,
    })
    const lpGeometry = isFractional ? new THREE.OctahedronGeometry(0.06, 0) : new THREE.TetrahedronGeometry(0.07, 0)
    const lpMesh = new THREE.Mesh(lpGeometry, lpMaterial)
    lpMesh.position.copy(lpSolutionPoint)
    // Ensure LP marker renders on top of the main mesh and edges
    lpMesh.renderOrder = 120
      ; (lpMesh as any).userData = { ...(lpMesh as any).userData, lpMarker: true }
    // Avoid frustum culling issues for small markers
    lpMesh.frustumCulled = false
    group.add(lpMesh)
  }
}
